import type { TFunction } from "i18next"

/**
 * OKS-11 Resolved Decision D1 — labeled role vocabulary.
 *
 * Backend stores two fields that together describe a user's attachment to an
 * account: the `target_account.type` (`'brand' | 'agency' | null`) and the
 * `role` (a value from `User::ROLES`: `'brand' | 'creator' | 'agency' |
 * 'admin'`). Frontend never renders the raw enum — it resolves through this
 * single helper so every surface (accept page, pending list, future
 * invite-create drawers in OKS-9/10) agrees on the contextual label.
 *
 * Mapping (locked BEFORE OKS-9/10 ship their inviter-side selectors):
 *
 * | target_account.type | role       | i18n key                     | EN                 | FR                         |
 * | ------------------- | ---------- | ---------------------------- | ------------------ | -------------------------- |
 * | `'brand'`           | `'brand'`  | `role_labels.brand.member`   | Editor             | Éditeur                    |
 * | `'brand'`           | `'admin'`  | `role_labels.brand.admin`    | Admin              | Admin                      |
 * | `'agency'`          | `'agency'` | `role_labels.agency.member`  | Analyst            | Analyste                   |
 * | `'agency'`          | `'admin'`  | `role_labels.agency.admin`   | Account Manager    | Responsable de compte      |
 * | `null`              | `'admin'`  | `role_labels.oks.admin`      | Admin              | Admin                      |
 * | `null`              | `'creator'`| `role_labels.oks.member`     | Crew Member        | Membre du crew             |
 *
 * The `role_labels.*` namespace deliberately does NOT overlap with the
 * OKS-5 `roles.*` namespace (used by `components/role-badge.tsx` for the
 * user's abstract role: admin/agency/brand/creator). That namespace is a
 * flat map of role → display string; this one is a two-level map that
 * contextualises User::ROLES by account type so, e.g., a `brand` role on a
 * brand account surfaces as "Editor" rather than the raw enum "Brand".
 *
 * `null` + `admin` → OKS Admin path. `null` + `creator` → Creator path
 * (invitee has no pre-existing brand/agency tenancy; they join as a crew
 * member of the inviting brand).
 *
 * This helper is the ONLY place the mapping lives. Do not duplicate the enum
 * → label lookup anywhere else — new callers import `roleLabel` + pass the
 * component's `t` function.
 *
 * The switch statement below uses literal string arguments on every branch
 * (e.g. `t("role_labels.brand.admin")`) so the i18n health scanner
 * (spec/inertia_i18n_health_spec.rb) can statically prove all six keys are
 * reachable. Do NOT refactor this into a dynamic template string — the
 * scanner only matches string-literal arguments to `t(...)`.
 */

export type InvitationAccountType = "brand" | "agency" | null
/**
 * Invitation.role mirrors `User::ROLES` on the backend (see
 * `app/models/invitation.rb` — `validates :role, inclusion: {in: User::ROLES}`).
 * That's four values, not an admin/member pair.
 */
export type InvitationRole = "brand" | "creator" | "agency" | "admin"

export interface RoleLabelArgs {
  accountType: InvitationAccountType
  role: InvitationRole
}

/**
 * Resolve the i18n key for a given (accountType, role) pair. Exposed
 * separately from `roleLabel` so tests can assert the key routing without
 * standing up an i18next instance. Returns `null` for combinations outside
 * the D1 mapping (stale-cache / forward-compat fallback path).
 */
export function roleLabelKey({
  accountType,
  role,
}: RoleLabelArgs): string | null {
  if (accountType === "brand") {
    if (role === "brand") return "role_labels.brand.member"
    if (role === "admin") return "role_labels.brand.admin"
  }
  if (accountType === "agency") {
    if (role === "agency") return "role_labels.agency.member"
    if (role === "admin") return "role_labels.agency.admin"
  }
  if (accountType === null) {
    if (role === "admin") return "role_labels.oks.admin"
    if (role === "creator") return "role_labels.oks.member"
  }
  return null
}

/**
 * Render the contextual role label through the caller's `t` function.
 *
 * Callers pass their component-local `t` (from `useTranslation()`) rather
 * than have this helper import `i18next` directly — keeps the helper pure,
 * test-friendly, and re-rendered on locale change.
 *
 * Falls back to the neutral `roles.<role>` label (owned by common.en/fr.yml —
 * see OKS-5 role-badge) for any (accountType, role) combination outside
 * the table above; in practice the backend invariant `role_matches_target`
 * prevents those combinations from ever reaching the frontend, but we defend
 * against stale caches by rendering *something* readable instead of blanking.
 */
export function roleLabel(
  t: TFunction,
  { accountType, role }: RoleLabelArgs,
): string {
  if (accountType === "brand") {
    if (role === "brand") return t("role_labels.brand.member")
    if (role === "admin") return t("role_labels.brand.admin")
  }
  if (accountType === "agency") {
    if (role === "agency") return t("role_labels.agency.member")
    if (role === "admin") return t("role_labels.agency.admin")
  }
  if (accountType === null) {
    if (role === "admin") return t("role_labels.oks.admin")
    if (role === "creator") return t("role_labels.oks.member")
  }
  // Stale cache / forward-compat fallback — never reachable given backend
  // `role_matches_target` validation, but belt-and-braces: surface the raw
  // OKS-5 role badge label instead of returning empty string.
  return t(`roles.${role}`)
}
